import { ApplicationService, DatabaseService, DataCollectionService, SchemaService, ServerService } from '@amzn/awscat-portfolio-assessment-service-client';
import { 
    ApplicationBulkUpdateParams, Contact, Database,
    DatabaseBulkUpdateParams, DeserializedApplication, 
    DeserializedServer, ServerBulkUpdateParams, UpdateAssignmentDataCollectionParams 
} from '@amzn/awscat-portfolio-assessment-types';
import { FlashContextInterface, FlashType, withFlashContext } from '@amzn/awscat-react-components';
import { Box, Button, Grid, Header, NonCancelableCustomEvent,
    Popover,
    Select, SelectProps, SpaceBetween, Tabs } from '@amzn/awsui-components-react';
import StatusIndicator from '@amzn/awsui-components-react/polaris/status-indicator';
import { useContext, useEffect, useState } from 'react';
import { match, useRouteMatch } from 'react-router';
import { useIsMounted } from 'react-tidy';

import { constants } from '../../../common/Localization';
import { ColumnDefinition } from '../../../common/table/types';
import { ErrorWithResponse, MatchParam } from '../../../common/types';
import { generatePluralStr } from '../../../common/util';
import { ContactManagementContext } from '../../../contexts/ContactManagementContext';
import { ColumnOrderType } from '../../portfolio-data-asset/constants';
import { SchemaPropertyMap } from '../../portfolio-data-asset/types';
import { generateColumnDefs } from '../../portfolio-data-asset/utils';
import { EXCLUDED_APPLICATION_COLUMNS, EXCLUDED_DATABASE_COLUMNS, EXCLUDED_SERVER_COLUMNS } from './constants';
import ContactManagementOwnershipAssetTable from './ContactManagementOwnershipAssetTable';
import { ApplicationWithMappings, DatabaseWithMappings} from './interfaces';

type Props = {
    contacts: Contact[];
} & FlashContextInterface;

const applicationService = new ApplicationService();
const serverService = new ServerService();
const databaseService = new DatabaseService();
const schemaService = new SchemaService();
const dataCollectionService = new DataCollectionService();

function ContactManagementOwnership({ contacts, flash }: Props): JSX.Element {
    const match: match<MatchParam> | null = useRouteMatch({
        path: '/portfolio/:portfolioId',
    });
    const portfolioId = match?.params?.portfolioId;
    const isMounted = useIsMounted();
    
    const [ contactOptions, setContactOptions ] = useState<SelectProps.Options>([]);
    const [ selectedContact, setSelectedContact ] = useState<SelectProps.Option | null>(null);
    const [ applicationColumnDefinitions, setApplicationColumnDefinitions ] = useState<ColumnDefinition<ApplicationWithMappings>[]>([]);
    const [ serverColumnDefinitions, setServerColumnDefinitions ] = useState<ColumnDefinition<DeserializedServer>[]>([]);
    const [ databaseColumnDefinitions, setDatabaseColumnDefinitions ] = useState<ColumnDefinition<DatabaseWithMappings>[]>([]);
    const [ loadingApplications, setLoadingApplications ] = useState(false);
    const [ loadingServers, setLoadingServers ] = useState(false);
    const [ loadingDatabases, setLoadingDatabases ] = useState(false);
    const [ selectedApplications, setSelectedApplications ] = useState<ApplicationWithMappings[]>([]);
    const [ selectedServers, setSelectedServers ] = useState<DeserializedServer[]>([]);
    const [ selectedDatabases, setSelectedDatabases ] = useState<DatabaseWithMappings[]>([]);
    const [ activeTab, setActiveTab ] = useState('application');
    const [ updateOwnershipLoading, setUpdateOwnershipLoading ] = useState(false);
    const { applications, servers, databases, updateApplications, updateServers, updateDatabases } = useContext(ContactManagementContext);

    const loadContactOptions = () => {
        if (!contacts) {
            return;
        }
        const options: SelectProps.Options = contacts.map(contact => {
            return {
                label: contact.name,
                value: contact.contactId,
                description: contact.email,
            };
        });
        if (isMounted()) {
            setContactOptions(options);
        }
    };

    useEffect(() => {
        loadContactOptions();
        loadApplications();
        loadServers();
        loadDatabases();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ contacts ]);

    const loadApplications = async () => {
        if (!portfolioId || !isMounted()) {
            return;
        }

        setLoadingApplications(true);
        setSelectedApplications([]);
        try {
            const applications: DeserializedApplication[] = await applicationService.list(portfolioId, true);
            const applicationsWithMapping: ApplicationWithMappings[] = applications.map(application => {
                return {
                    ...application,
                    serverMapping: application.serverMappings ? constants.MAPPED : constants.UNMAPPED,
                    databaseMapping: application.databaseMappings ? constants.MAPPED : constants.UNMAPPED
                };
            });
            if (!isMounted()) {
                return;
            }
            updateApplications(applicationsWithMapping);
            const applicationSchemas = await schemaService.listAttributes('applications', portfolioId);
         
            const headers: SchemaPropertyMap = {};
            applicationSchemas.forEach(schema => {
                headers[schema.value] = schema;
            });
            headers['databaseMapping'] = {
                value: 'databaseMapping',
                text: constants.DATABASE_MAPPED,
                messages: {
                    updateForbidden: constants.DATABASE_MAPPING_CANNOT_BE_UPDATED
                }
            };
            headers['serverMapping'] = {
                value: 'serverMapping',
                text: constants.SERVER_MAPPED,
                messages: {
                    updateForbidden: constants.SERVER_MAPPING_CANNOT_BE_UPDATED
                }
            };

            const columnDefinitions = generateColumnDefs<DeserializedApplication>(applicationsWithMapping, headers, 
                ColumnOrderType.CONTACT_MANAGEMENT_APPLICATION, EXCLUDED_APPLICATION_COLUMNS);
            if (isMounted()) {
                setApplicationColumnDefinitions(columnDefinitions);
            }
        } catch (err) {
            if (isMounted()) {
                flash.alert(FlashType.error,  (err as Error).message, constants.APPLICATION_ERROR_LOADING);
                setSelectedApplications([]);
            }
        } finally {
            if (isMounted()) {
                setLoadingApplications(false);
            }
        }
    };

    const loadServers = async () => {
        if (!portfolioId || !isMounted()) {
            return;
        }

        setLoadingServers(true);
        setSelectedServers([]);
        try {
            const servers = await serverService.list(portfolioId, true);
            if (isMounted()) {
                updateServers(servers);
            }

            const serverSchemas = await schemaService.listAttributes('servers', portfolioId);
         
            const headers: SchemaPropertyMap = {};
            serverSchemas.forEach(schema => {
                headers[schema.value] = schema;
            });
            const columnDefinitions = generateColumnDefs<DeserializedServer>(servers, headers,
                ColumnOrderType.CONTACT_MANAGEMENT_SERVER, EXCLUDED_SERVER_COLUMNS);
            if (isMounted()) {
                setServerColumnDefinitions(columnDefinitions);
            }
        } catch (err) {
            flash.alert(FlashType.error,  (err as Error).message, constants.SERVER_ERROR_LOADING);
            updateServers([]);
        } finally {
            if (isMounted()) {
                setLoadingServers(false);
            }
        }
    };

    const loadDatabases = async () => {
        if (!portfolioId || !isMounted()) {
            return;
        }
        setLoadingDatabases(true);
        setSelectedDatabases([]);
        try {
            const databases = await databaseService.list(portfolioId, true);
            const databasesWithMapping: DatabaseWithMappings[] = databases.map(database => {
                return {
                    ...database,
                    applicationMapping: database.applicationMappings ? constants.MAPPED : constants.UNMAPPED,
                    serverMapping: database.serverMappings ? constants.MAPPED : constants.UNMAPPED,
                };
            });
            if (!isMounted()) {
                return;
            }
            updateDatabases(databasesWithMapping);

            const databaseSchemas = await schemaService.listAttributes('databases', portfolioId);
         
            const headers: SchemaPropertyMap = {};
            databaseSchemas.forEach(schema => {
                headers[schema.value] = schema;
            });

            headers['applicationMapping'] = {
                value: 'applicationMapping',
                text: constants.APPLICATION_MAPPED,
                messages: {
                    updateForbidden: constants.APPLICATION_MAPPING_CANNOT_BE_UPDATED
                }
            };
            headers['serverMapping'] = {
                value: 'serverMapping',
                text: constants.SERVER_MAPPED,
                messages: {
                    updateForbidden: constants.SERVER_MAPPING_CANNOT_BE_UPDATED
                }
            };
            const columnDefinitions = generateColumnDefs<Database>(databasesWithMapping, headers,
                ColumnOrderType.CONTACT_MANAGEMENT_DATABASE, EXCLUDED_DATABASE_COLUMNS);
           
            if (isMounted()) {
                setDatabaseColumnDefinitions(columnDefinitions);
            }
        } catch (err) {
            if (isMounted()) {
                flash.alert(FlashType.error,  (err as Error).message, constants.DATABASE_ERROR_LOADING);
                setSelectedDatabases([]);
            }
        } finally {
            if (isMounted()) {
                setLoadingDatabases(false);
            }
        }
    };

    const selectedContactChanged = (event: NonCancelableCustomEvent<SelectProps.ChangeDetail>) => {
        setSelectedContact(event.detail.selectedOption);
    };

    const onChangeOwnershipError = (errorMessage: string, assetType: string) => {
        // this indicates that the user did not create the questionnaire, error message from data collection
        if (errorMessage.includes('Cannot find questionnaire with id')) {
            flash.alert(FlashType.error, constants.QUESTIONNAIRE_ALREADY_EXISTS, `${constants.UNABLE_TO_CHANGE_OWNERSHIP} ${constants.APPLICATIONS_LOWERCASE}`);
        } else {
            flash.alert(FlashType.error, errorMessage, `${constants.UNABLE_TO_CHANGE_OWNERSHIP} ${assetType}`);
        }
    };

    const changeOwnership = async () => {
        if (!portfolioId || !selectedContact || !selectedContact.value || !selectedContact.description) {
            return;
        }

        if (activeTab === 'application') {
            const selectedApplicationIds: string[] = selectedApplications.map(application => application.applicationId);
            const data: ApplicationBulkUpdateParams = {
                fieldName: 'businessOwnerId',
                fieldValue: selectedContact.value,
                portfolioId,
                applicationIds: selectedApplicationIds
            };
            try {
                setUpdateOwnershipLoading(true);
                await updateAssignment(selectedApplicationIds, 'application', selectedContact.description);
                await applicationService.update(data);
                loadApplications();
                flash.alert(FlashType.success, 
                    `${selectedApplications.length} ${generatePluralStr(selectedApplications.length, constants.APPLICATION_LOWERCASE)} 
                    ${constants.BULK_UPDATE_SUCCESS_HEADING}`, '');
            } catch (err) {
                if (isMounted()) {
                    onChangeOwnershipError((err as ErrorWithResponse).response?.data?.message ?? (err as Error).message, constants.APPLICATIONS_LOWERCASE);
                }
            } finally {
                if (isMounted()) {
                    setUpdateOwnershipLoading(false);
                }
            }
        } else if (activeTab === 'server') {
            const selectedServerIds: string[] = selectedServers.map(server => server.serverId);
            const data: ServerBulkUpdateParams = {
                fieldName: 'serverSupportId',
                fieldValue: selectedContact.value,
                portfolioId,
                serverIds: selectedServerIds
            };
            try {
                setUpdateOwnershipLoading(true);
                await updateAssignment(selectedServerIds, 'server', selectedContact.description);
                await serverService.update(data);
                loadServers();
                flash.alert(FlashType.success, 
                    `${selectedServers.length} ${generatePluralStr(selectedServers.length, constants.SERVER_LOWERCASE)} 
                    ${constants.BULK_UPDATE_SUCCESS_HEADING}`, '');
            } catch (err) {
                if (isMounted()) {
                    onChangeOwnershipError((err as ErrorWithResponse).response?.data?.message ?? (err as Error).message, constants.SERVERS_LOWERCASE);
                }
            } finally {
                if (isMounted()) {
                    setUpdateOwnershipLoading(false);
                }
            }
        } else if (activeTab === 'database') {
            const selectedDatabaseIds: string[] = selectedDatabases.map(database => database.databaseId);
            const data: DatabaseBulkUpdateParams = {
                fieldName: 'dbOwnerId',
                fieldValue: selectedContact.value,
                portfolioId,
                databaseIds: selectedDatabaseIds
            };
            try {
                setUpdateOwnershipLoading(true);
                await databaseService.update(data);
                loadDatabases();
                flash.alert(FlashType.success, 
                    `${selectedDatabases.length} ${generatePluralStr(selectedDatabases.length, constants.DATABASE_LOWERCASE)} 
                    ${constants.BULK_UPDATE_SUCCESS_HEADING}`, '');
            } catch (err) {
                if (isMounted()) {
                    onChangeOwnershipError((err as ErrorWithResponse).response?.data?.message ?? (err as Error).message, constants.DATABASES_LOWERCASE);
                }
            } finally {
                if (isMounted()) {
                    setUpdateOwnershipLoading(false);
                }
            }
        }
    };

    const updateAssignment = async (resourceIds: string[], resourceType: string, newUserId: string) => {
        if (!portfolioId) {
            return;
        }
        const data: UpdateAssignmentDataCollectionParams = {
            resourceIds,
            resourceType,
            newUserId
        };
        await dataCollectionService.updateAssignment(portfolioId, data);
    };

    const isChangeOwnershipDisabled: boolean = (activeTab === 'application' && selectedApplications.length === 0)
        || (activeTab === 'server' && selectedServers.length === 0)
        || (activeTab === 'database' && selectedDatabases.length === 0)
        || !selectedContact;

    const selectUser = (
        <Grid gridDefinition={[{colspan: 4}, {colspan: 3, offset: {default: 5}}]} data-testid='userGrid'>
            <Select
                options={contactOptions}
                selectedOption={selectedContact}
                onChange={selectedContactChanged}
                filteringType='auto'
                empty={constants.NO_CONTACT_DATA_FOUND}
            />
            <Box float='right'>
                <Button
                    data-testid='changeOwnerButton'
                    disabled={isChangeOwnershipDisabled}
                    onClick={changeOwnership}
                    loading={updateOwnershipLoading}
                    variant='primary'
                >
                    {constants.ASSIGN_OWNERSHIP}
                </Button>
            </Box>
        </Grid>
    );

    const applicationTabContent = (
        <ContactManagementOwnershipAssetTable<ApplicationWithMappings>
            resourceName={constants.APPLICATIONS}
            items={applications}
            columnDefinitions={applicationColumnDefinitions}
            onSelectionChange={setSelectedApplications as () => void}
            loading={loadingApplications}
            href={`/portfolio/${portfolioId}/management/assets/applications`}
        />
    );

    const serverTabContent = (
        <ContactManagementOwnershipAssetTable<DeserializedServer>
            resourceName={constants.SERVERS}
            items={servers}
            columnDefinitions={serverColumnDefinitions}
            onSelectionChange={setSelectedServers as () => void}
            loading={loadingServers}
            href={`/portfolio/${portfolioId}/management/assets/servers`}
        />
    );

    const databaseTabContent = (
        <ContactManagementOwnershipAssetTable<DatabaseWithMappings>
            resourceName={constants.DATABASES}
            items={databases}
            columnDefinitions={databaseColumnDefinitions}
            onSelectionChange={setSelectedDatabases as () => void}
            loading={loadingDatabases}
            href={`/portfolio/${portfolioId}/management/assets/databases`}
        />
    );

    const assetTabs = (
        <Tabs
            data-testid='assetTabs'
            tabs={[
                {
                    label: constants.APPLICATIONS,
                    id: 'application',
                    content: applicationTabContent,
                },
                {
                    label: constants.SERVERS,
                    id: 'server',
                    content: serverTabContent,
                },
                {
                    label: constants.DATABASES,
                    id: 'database',
                    content: databaseTabContent,
                }
            ]}
            onChange={({ detail }) => setActiveTab(detail.activeTabId)}
            activeTabId={activeTab}
        />
    );

    return (
        <SpaceBetween size='s'>
            <Popover
                dismissButton={false}
                position='right'
                triggerType='text'
                size='large'
                content={
                    <StatusIndicator type='info'>{constants.ASSIGN_OWNER_TIP}</StatusIndicator>
                }
            >
                <Header>{constants.ASSIGN_OWNERSHIP}</Header>
            </Popover>
            {selectUser}
            {assetTabs}
        </SpaceBetween>
    );
}

export default withFlashContext(ContactManagementOwnership);
